import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, Ruler, Globe, Zap, ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { setCanonical, setMeta } from '../utils/seo';
import { HomeSchemas } from './SEOSchemas';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#E8E2D9] rounded-sm">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left hover:bg-[#FAFAFA] transition-colors" data-testid={`faq-${q.slice(0,20).replace(/\s/g,'-').toLowerCase()}`}>
        <span className="font-medium text-[#1C1917] pr-4">{q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-[#78716C] flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#78716C] flex-shrink-0" />}
      </button>
      {open && <div className="px-5 pb-5 text-sm text-[#78716C] leading-relaxed">{a}</div>}
    </div>
  );
}

export default function HomePage() {
  const [brands, setBrands] = useState([]);
  const [tip, setTip] = useState('');
  const [featured, setFeatured] = useState(null);
  const [stats, setStats] = useState({ sessions: 0, brands: 0 });
  const [search, setSearch] = useState('');

  useEffect(() => {
    setCanonical('/');
    setMeta(null, null);
    axios.get(`${API}/brands?popular=true`).then(r => setBrands(r.data)).catch(() => {});
    axios.get(`${API}/tip-of-the-week`).then(r => setTip(r.data.tip)).catch(() => {});
    axios.get(`${API}/featured-brand`).then(r => setFeatured(r.data.brand)).catch(() => {});
    axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(() => {});
    axios.post(`${API}/stats/session`).catch(() => {});
  }, []);

  const filteredBrands = search ? brands.filter(b => b.name.toLowerCase().includes(search.toLowerCase())) : brands;
  const faqs = [
    { q: 'How do I find my clothing size?', a: 'Use our "Find My Size" tool. Enter your body measurements (chest, waist, hips, or foot length) and we\'ll recommend your size across 50+ brands instantly.' },
    { q: 'Are the size conversions accurate?', a: 'Our size charts are based on official brand sizing guides and industry standards. However, sizes can vary by style and cut. Always check the specific item\'s size chart when purchasing.' },
    { q: 'Why do sizes differ between brands?', a: 'There\'s no universal sizing standard. Each brand uses different fit models, measurement systems, and target demographics. European brands tend to run smaller than US brands.' },
    { q: 'Is TrueFitGuide free to use?', a: 'Yes! TrueFitGuide is 100% free. We provide size charts, conversion tools, and fit recommendations at no cost.' },
    { q: 'Is this professional sizing advice?', a: 'No. TrueFitGuide provides general size guidance based on standard measurements. Sizes are approximate. Always verify with the retailer before purchasing.' },
  ];

  return (
    <div>
      <HomeSchemas />
      {/* Hero */}
      <section className="relative bg-gradient-to-b from-[#FAF8F4] to-[#FAFAFA] py-16 sm:py-24 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A028] mb-4">Free Size Guide for 50+ Brands</p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-6xl font-light tracking-tighter text-[#1C1917] leading-none mb-6">
            Find Your <span className="font-bold italic text-[#C5A028]">Perfect Fit</span><br />Across Every Brand
          </h1>
          <p className="text-[#78716C] text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed font-light">
            Stop guessing. Convert sizes between US, UK, EU, Japan & Korea instantly. 50+ brands. Free forever.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/find-my-size" className="inline-flex items-center justify-center gap-2 bg-[#1C1917] text-white px-8 py-4 font-medium text-sm hover:bg-[#C5A028] transition-all duration-300 hover:-translate-y-0.5" data-testid="hero-find-my-size-btn">
              <Ruler className="w-4 h-4" /> Find My Size
            </Link>
            <Link to="/compare" className="inline-flex items-center justify-center gap-2 border-2 border-[#1C1917] text-[#1C1917] px-8 py-4 font-medium text-sm hover:bg-[#1C1917] hover:text-white transition-all duration-300" data-testid="hero-compare-btn">
              Compare Brands <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex items-center justify-center gap-6 text-sm text-[#78716C]">
            <span><strong className="text-[#1C1917]">{stats.sessions?.toLocaleString()}</strong> sizes found</span>
            <span className="w-1 h-1 bg-[#C5A028] rounded-full" />
            <span><strong className="text-[#1C1917]">50+</strong> brands</span>
            <span className="w-1 h-1 bg-[#C5A028] rounded-full" />
            <span><strong className="text-[#1C1917]">6</strong> regions</span>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 -mt-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { to: '/find-my-size', icon: Ruler, label: 'Find My Size', desc: 'Body measurements to size' },
            { to: '/compare', icon: ArrowRight, label: 'Compare Brands', desc: 'Nike M = Zara what?' },
            { to: '/brands', icon: Globe, label: 'All Brands', desc: '50+ brand size charts' },
            { to: '/body-type-guide', icon: Zap, label: 'Style Guide', desc: 'Best fits for your body' },
          ].map(a => (
            <Link key={a.to} to={a.to} className="bg-white p-5 border border-[#E8E2D9] hover:border-[#C5A028] hover:shadow-md transition-all duration-300 group" data-testid={`quick-${a.label.toLowerCase().replace(/\s/g,'-')}`}>
              <a.icon className="w-5 h-5 text-[#C5A028] mb-3 group-hover:scale-110 transition-transform" />
              <p className="font-semibold text-sm text-[#1C1917]">{a.label}</p>
              <p className="text-xs text-[#78716C] mt-1">{a.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Tip of the Week */}
      {tip && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-10">
          <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-5 flex gap-3 items-start">
            <ShieldCheck className="w-5 h-5 text-[#C5A028] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase text-[#C5A028] mb-1">Tip of the Week</p>
              <p className="text-sm text-[#78716C]">{tip}</p>
            </div>
          </div>
        </section>
      )}

      {/* Featured Brand */}
      {featured && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-10">
          <div className="bg-white border border-[#E8E2D9] p-6 sm:p-8">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#C5A028] mb-2">Featured Brand of the Week</p>
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1C1917] mb-2">{featured.name}</h3>
            <p className="text-sm text-[#78716C] mb-3">{featured.description}</p>
            <p className="text-sm text-[#1C1917] font-medium mb-4">Fit: {featured.fit_guide}</p>
            <Link to={`/brand/${featured.slug}`} className="inline-flex items-center gap-2 text-sm font-semibold text-[#C5A028] hover:text-[#1C1917] transition-colors" data-testid="featured-brand-link">
              View {featured.name} Size Chart <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Browse Brands */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-semibold tracking-widest uppercase text-[#C5A028] mb-2">Popular Brands</p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-light tracking-tighter text-[#1C1917]">Size Charts You Need</h2>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716C]" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search brands..." className="pl-10 pr-4 py-2.5 bg-[#FAFAFA] border border-[#E8E2D9] text-sm w-64 focus:border-[#C5A028] focus:outline-none transition-colors" aria-label="Search brands" data-testid="brand-search-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredBrands.slice(0, 15).map(b => (
            <Link key={b.slug} to={`/brand/${b.slug}`} className="bg-white p-4 border border-[#E8E2D9] hover:border-[#C5A028] hover:shadow-md transition-all duration-300 group" data-testid={`brand-card-${b.slug}`}>
              <p className="font-semibold text-[#1C1917] group-hover:text-[#C5A028] transition-colors">{b.name}</p>
              <p className="text-xs text-[#78716C] mt-1">{b.country_origin}</p>
              <p className="text-xs text-[#78716C] mt-1 line-clamp-2">{b.categories?.length || 0} categories</p>
            </Link>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/brands" className="inline-flex items-center gap-2 text-sm font-semibold text-[#C5A028] hover:text-[#1C1917] transition-colors" data-testid="view-all-brands-link">View All 50+ Brands <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-16" id="how-it-works">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold tracking-widest uppercase text-[#C5A028] mb-2">Simple & Free</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-light tracking-tighter text-[#1C1917]">How TrueFitGuide Works</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {[
            { step: '01', title: 'Enter Measurements', desc: 'Use a tape measure to get your chest, waist, hips, or foot length in cm or inches.' },
            { step: '02', title: 'Pick Your Brand', desc: 'Select from 50+ brands including Nike, Zara, H&M, Levi\'s, and more.' },
            { step: '03', title: 'Get Your Size', desc: 'Instant size recommendation with regional conversions (US, UK, EU, JP, KR).' },
          ].map(s => (
            <div key={s.step} className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-[#FAF8F4] border border-[#E8E2D9]">
                <span className="font-['Cormorant_Garamond'] text-lg font-bold text-[#C5A028]">{s.step}</span>
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#1C1917] mb-2">{s.title}</h3>
              <p className="text-sm text-[#78716C] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 mt-16">
        <div className="text-center mb-8">
          <h2 className="font-['Cormorant_Garamond'] text-3xl font-light tracking-tighter text-[#1C1917]">Frequently Asked Questions</h2>
        </div>
        <div className="flex flex-col gap-3">
          {faqs.map(f => <FAQ key={f.q} q={f.q} a={f.a} />)}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 mt-16">
        <div className="bg-[#FAF8F4] border border-[#E8E2D9] p-6">
          <h3 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[#1C1917] mb-2">Important Disclaimer</h3>
          <p className="text-xs text-[#78716C] leading-relaxed">This is not professional sizing advice. All size charts, conversions, and recommendations shown on TrueFitGuide are approximations for informational purposes only. Actual sizes may vary by style, cut, and individual item. Always check the retailer's specific size chart before purchasing. TrueFitGuide is not affiliated with any brand listed and is not responsible for incorrect purchases.</p>
        </div>
      </section>

      {/* Sponsored Partners */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 mt-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a href="https://amzn.to/41436bl" target="_blank" rel="noopener noreferrer" className="block border border-[#E8E2D9] p-5 hover:border-[#C5A028] hover:shadow-md transition-all group" data-testid="home-amazon" style={{background: 'linear-gradient(135deg, #FFFBF0 0%, #FFF5E0 50%, #FFFAF2 100%)'}}>
            <p className="text-xs font-semibold tracking-widest uppercase text-[#C5A028] mb-2">Sponsored Partner</p>
            <p className="font-semibold text-[#1C1917] text-sm group-hover:text-[#C5A028] transition-colors mb-1">Shop Fashion on Amazon</p>
            <p className="text-xs text-[#78716C] mb-3">Found your size? Shop 50+ brands with free returns.</p>
            <p className="text-lg mb-2 tracking-wider">👗 👚 👖 👠 👢 👜</p>
            <span className="inline-flex items-center gap-1.5 bg-[#FF9900] text-white px-3 py-1 text-xs font-bold rounded-sm group-hover:bg-[#E88B00] transition-colors">Shop Now</span>
            <p className="text-[10px] text-[#78716C] mt-2 italic">Ad: As an Amazon Associate, TrueFitGuide earns from qualifying purchases. This is a paid affiliate link.</p>
          </a>
          <a href="https://tonehealing.com" target="_blank" rel="noopener noreferrer" className="block overflow-hidden border border-[#E8E2D9] hover:border-[#9B7FD4] hover:shadow-md transition-all group" data-testid="home-sponsor" style={{background: 'linear-gradient(135deg, #F5F0FF 0%, #EDE4FF 50%, #F9F5FF 100%)'}}>
            <div className="flex items-center gap-3 p-5">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold tracking-widest uppercase text-[#7C5CBF] mb-2">Sponsored</p>
                <p className="font-semibold text-[#1C1917] text-sm leading-tight mb-1">Support Your Mind & Body with <span className="text-[#7C5CBF]">Sound Frequencies</span></p>
                <p className="text-xs text-[#78716C] mb-2">Fall asleep in minutes. Melt anxiety away. Free sound therapy — just press play.</p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className="text-[10px] bg-white/80 text-[#78716C] px-1.5 py-0.5 border border-[#E8E2D9] rounded-full">🌙 Sleep</span>
                  <span className="text-[10px] bg-white/80 text-[#78716C] px-1.5 py-0.5 border border-[#E8E2D9] rounded-full">🧘 Calm</span>
                  <span className="text-[10px] bg-white/80 text-[#78716C] px-1.5 py-0.5 border border-[#E8E2D9] rounded-full">🎯 Focus</span>
                  <span className="text-[10px] bg-white/80 text-[#78716C] px-1.5 py-0.5 border border-[#E8E2D9] rounded-full">💆 Pain Relief</span>
                </div>
                <span className="inline-flex items-center gap-1.5 bg-[#7C5CBF] text-white px-3 py-1 text-xs font-bold rounded-sm group-hover:bg-[#6B4FA8] transition-colors">Click Here</span>
              </div>
              <div className="flex-shrink-0 hidden sm:block">
                <img src="https://static.prod-images.emergentagent.com/jobs/e586dc18-7e49-4762-9a32-fdcca74463b6/images/4934b7f9e8db17a37a56475081896b2c65939f6c0ba1eb7b6972c30393fe8a80.png" alt="Sound therapy" className="w-20 h-20 rounded-full object-cover" />
              </div>
            </div>
            <div className="px-5 pb-2">
              <p className="text-[10px] text-[#9B8FC4] italic">Ad: This is a sponsored link to an affiliated website. You will leave TrueFitGuide.com.</p>
            </div>
          </a>
        </div>
      </section>

      {/* AdSense Placeholder */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 mt-10 mb-8">
        <div className="bg-white border border-dashed border-[#E8E2D9] p-8 text-center">
          <p className="text-xs text-[#78716C]">Advertisement</p>
          <p className="text-sm text-[#78716C] mt-1">Google AdSense - Pending approval</p>
        </div>
      </section>
    </div>
  );
}
